import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProgram } from '../../types/user.response';
import ProgramSelectionForm from './ProgramSelectionForm';
import ExamSelectionForm, { ExamEntry } from './ExamSelectionForm';
import { usersApi } from '../../services/api/users.api';
import { useAuth } from '../../contexts/AuthContext';
import { catalogApi } from '../../services/api/catalog.api';
import { CatalogProgram, CatalogCollege, CatalogExam } from '../../types/catalog.response';
import { userExamsApi } from '../../services/api/user-exams.api';

interface ProfileData {
    programs: UserProgram[];
    collegeId: string;
    entryYear: string;
}

export default function CompleteProfile() {
    const navigate = useNavigate();
    const { refreshAuth } = useAuth();

    const [step, setStep] = useState<1 | 2>(1);
    const [profileData, setProfileData] = useState<ProfileData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [programs, setPrograms] = useState<CatalogProgram[]>([]);
    const [colleges, setColleges] = useState<CatalogCollege[]>([]);
    const [exams, setExams] = useState<CatalogExam[]>([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [fetchedPrograms, fetchedColleges, fetchedExams] = await Promise.all([
                    catalogApi.getPrograms(),
                    catalogApi.getColleges(),
                    catalogApi.getExams(),
                ]);
                setPrograms(fetchedPrograms);
                setColleges(fetchedColleges);
                setExams(fetchedExams);
            } catch (err) {
                setError('Failed to load data. Please refresh and try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleStep1Complete = (selectedPrograms: UserProgram[], collegeId: string, entryYear: string) => {
        setProfileData({ programs: selectedPrograms, collegeId, entryYear });
        setStep(2);
    };

    const handleStep2Complete = async (selectedExams: ExamEntry[]) => {
        if (!profileData) return;
        try {
            setLoading(true);
            setError(null);
            await usersApi.updateProfile({
                entryYear: profileData.entryYear,
                collegeId: profileData.collegeId,
                programs: profileData.programs,
            });
            await Promise.all(
                selectedExams.map(entry =>
                    userExamsApi.addExam(entry.examId, entry.score, entry.scoreAlpha, 3, [])
                )
            );
            await refreshAuth();
            navigate('/');
        } catch (err) {
            console.error('Profile completion error:', err);
            setError(err instanceof Error ? err.message : 'Failed to save profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen max-w-xl mx-auto mt-16 md:mt-32 px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
                <p className="text-xs font-medium text-blue-600 mb-2">
                    Step {step} of 2
                </p>
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                    {step === 1 ? 'Complete your profile' : 'Prior placement exams'}
                </h1>
                <p className="text-sm text-gray-500 max-w-md">
                    {step === 1
                        ? 'Tell us about your academic plan so we can recommend the right courses for you.'
                        : 'Add any AP, IB, or placement exams you\'ve taken.We\'ll use your scores to skip requirements you\'ve already completed.'}
                </p>
            </div>

            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {step === 1 && (
                <ProgramSelectionForm
                    programs={programs}
                    colleges={colleges}
                    onComplete={handleStep1Complete}
                    loading={loading}
                    submitLabel="Continue"
                />
            )}

            {step === 2 && (
                <ExamSelectionForm
                    exams={exams}
                    onComplete={handleStep2Complete}
                    onBack={() => setStep(1)}
                    loading={loading}
                />
            )}
        </div>
    );
}
